import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-toastify';
import styles from './ResultsSync.module.css';
import { getClubResultSyncStatus, retryResultSyncJob } from 'api/resultApi';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import LoadingContent from 'components/NewDesign/LoadingContent';
import { convertDateTime } from 'utils/time';

/**
 * Results Sync report (docs/results-sync-queue-design.md in the portal repo):
 * club-wide view of the results sync queue, one card per meet with activity in
 * the retention window, most recent activity first. Failed jobs show the server
 * error and a Retry button. The meet details page shows only a failure banner
 * (ResultSyncFailureBanner) that links here for the detail.
 */
const ResultsSync = ({ clubID }) => {
  const [meets, setMeets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);
  const [retryingJobId, setRetryingJobId] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!clubID) return;
    setLoading(true);
    try {
      const response = await getClubResultSyncStatus(clubID);
      setMeets(response.data?.meets || []);
      setLastChecked(new Date());
    } catch (err) {
      console.error('[ResultsSync] Failed to fetch sync status:', err);
      toast.error('Failed to load results sync activity');
    } finally {
      setLoading(false);
    }
  }, [clubID]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleRetry = async (job) => {
    setRetryingJobId(job.syncJobID);
    try {
      await retryResultSyncJob(job.syncJobID);
      toast.success(
        `${job.eventType} ${job.roundType} ${job.roundNumber} requeued for processing`
      );
      await fetchStatus();
    } catch (err) {
      toast.error(
        err.response?.data?.message || 'Failed to retry the sync job'
      );
    } finally {
      setRetryingJobId(null);
    }
  };

  return (
    <div className={styles.report}>
      <div className={styles.explainerCard}>
        <p className={styles.explainer}>
          Results uploaded from the timing and field apps are processed in the
          background. This report shows recent uploads across the meets of your
          club and lets you retry any that failed. Sync records are kept for 7
          days (failed uploads for 30 days).
        </p>
        <div className={styles.refreshArea}>
          {lastChecked && (
            <span className={styles.checkedAt}>
              Checked at {lastChecked.toLocaleTimeString()}
            </span>
          )}
          <CustomNewButton
            variant="info"
            onClick={fetchStatus}
            label={loading ? 'Refreshing…' : 'Refresh'}
            disabled={loading}
          />
        </div>
      </div>

      <LoadingContent loading={loading && meets === null}>
        {meets && meets.length === 0 && (
          <div className={styles.emptyState}>
            No sync activity recorded for your club in the last 7 days.
          </div>
        )}

        {meets &&
          meets.map((meet) => {
            // The endpoint returns detail rows for every job not yet completed
            // (queued / processing / failed) — an empty list means all processed.
            const pendingJobs = meet.jobs || [];
            return (
              <div key={meet.meetID} className={styles.meetCard}>
                <div className={styles.meetHeader}>
                  <span className={styles.meetName}>{meet.meetName}</span>
                  <span className={styles.meetDate}>
                    {convertDateTime(meet.meetDate).date}
                  </span>
                  {meet.failed > 0 && (
                    <span className={styles.failedBadge}>
                      <AlertTriangle size={12} />
                      {meet.failed} failed
                    </span>
                  )}
                </div>

                <div className={styles.counts}>
                  <span className={styles.count}>
                    <strong>{meet.queued}</strong> queued
                  </span>
                  <span className={styles.count}>
                    <strong>{meet.processing}</strong> processing
                  </span>
                  <span className={styles.count}>
                    <strong>{meet.completed}</strong> completed
                  </span>
                  <span
                    className={`${styles.count} ${meet.failed > 0 ? styles.failedText : ''}`}
                  >
                    <strong>{meet.failed}</strong> failed
                  </span>
                </div>

                {pendingJobs.length === 0 ? (
                  <div className={styles.successState}>
                    <CheckCircle2 size={16} />
                    All uploaded results processed successfully.
                  </div>
                ) : (
                  <table className={styles.jobsTable}>
                    <thead>
                      <tr>
                        <th>Event</th>
                        <th>Round</th>
                        <th>Status</th>
                        <th>Attempts</th>
                        <th>Error</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingJobs.map((job) => (
                        <tr key={job.syncJobID}>
                          <td>{job.eventType}</td>
                          <td>
                            {job.roundType} {job.roundNumber}
                          </td>
                          <td
                            className={
                              job.status === 'Failed' ? styles.failedText : ''
                            }
                          >
                            {job.status}
                          </td>
                          <td>{job.attemptCount}</td>
                          <td className={styles.errorCell}>
                            {job.errorMessage || '—'}
                          </td>
                          <td>
                            {job.status === 'Failed' && (
                              <CustomNewButton
                                onClick={() => handleRetry(job)}
                                label={
                                  retryingJobId === job.syncJobID
                                    ? 'Retrying…'
                                    : 'Retry'
                                }
                                variant="primary2"
                                disabled={retryingJobId !== null}
                              />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
      </LoadingContent>
    </div>
  );
};

ResultsSync.propTypes = {
  clubID: PropTypes.string,
};

export default ResultsSync;
